import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "flux-vs-sdxl-comparison";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title: "FLUX vs SDXL for Agent Workflows - AgentFramer",
				},
				{
					name: "description",
					content:
						"FLUX vs SDXL parameters, prompt formats, and LoRA ecosystems. Steps, guidance scales, samplers, and the agent setup that uses both correctly.",
				},
				{
					property: "og:title",
					content: "FLUX vs SDXL for Agent Workflows",
				},
				{
					property: "og:description",
					content:
						"Concrete parameter recommendations and prompt formats for FLUX and SDXL in agent workflows.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					FLUX vs Stable Diffusion XL for agent workflows
				</h1>
				<p className="text-sm text-muted-foreground">
					Parameter recipes, prompt formats, and the model split that actually
					ships in production agents.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					The failure that picks the model for you
				</h2>
				<p>
					An agent generates a hero image with the prompt{" "}
					<em>a man holding a sign that says "OPEN"</em>. SDXL returns a sign
					with mangled glyphs that resemble Cyrillic-Wingdings. FLUX returns a
					sign that says OPEN. That single behaviour decides the model split for
					most production agents: anything with embedded copy goes to FLUX,
					everything else is a parameter argument. The argument is what this
					post is about.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					FLUX parameters that work
				</h2>
				<p>
					FLUX is a flow-matching model and tolerates aggressive step counts. It
					also ignores most of the weighting syntax that SDXL prompts depend on,
					so treat the prompt like a sentence, not a tag list.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">FLUX schnell.</strong> Steps 4,
						guidance 0, sampler euler, scheduler simple. Negative prompts are
						ignored — do not bother sending one. Dimensions 1024x1024 or
						1024x1792 for vertical. Use this for drafts the agent will throw
						away.
					</li>
					<li>
						<strong className="text-foreground">FLUX dev.</strong> Steps 28-30,
						guidance 3.5, sampler euler, scheduler simple. No negative prompt
						needed. 1024x1024 native; 1216x832 and 832x1216 are stable.
						Non-commercial license — keep it out of customer-facing pipelines.
					</li>
					<li>
						<strong className="text-foreground">FLUX 1.1 pro.</strong> Hosted
						defaults handle the sampler, steps, and scheduler. Send the prompt,
						aspect ratio, and seed. Commercial licence, production default.
					</li>
				</ul>
				<p>
					Pushing FLUX dev past 30 steps gives you the same image with a longer
					bill. Pushing schnell past 4 actively degrades the output.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					SDXL parameters that work
				</h2>
				<p>
					SDXL is the opposite of FLUX in almost every respect: classifier-free
					guidance is essential, negative prompts are essential, and the sampler
					choice changes the result.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">Base SDXL.</strong> Steps 25-30,
						guidance 7.0, sampler DPM++ 2M, scheduler Karras. Always send a{" "}
						<code>negative_prompt</code> — at minimum{" "}
						<code>
							blurry, deformed, extra fingers, watermark, text, low quality
						</code>
						. Native 1024x1024; 1152x896 and 896x1152 train well. Anything
						smaller than 768 on a side falls apart.
					</li>
					<li>
						<strong className="text-foreground">SDXL Lightning.</strong> Steps
						4-8, guidance 1.0-2.0, sampler DPM++ 2M, scheduler Karras. Negative
						prompt still helps. Sub-second generation; ideal for batch.
					</li>
					<li>
						<strong className="text-foreground">SDXL Turbo.</strong> Steps 1-4,
						guidance 1.0, sampler euler ancestral. Quality drops below Lightning
						but the latency wins for streaming previews.
					</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					Prompt format: prose vs tags
				</h2>
				<p>
					FLUX wants natural prose — punctuation, clauses, descriptive phrases.
					Weighting like <code>(red dress:1.4)</code> is parsed as literal text.
					SDXL wants weighted tags separated by commas, with a BREAK token
					between subject and style chunks, and a populated negative prompt. The
					same idea looks completely different in each request.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`// FLUX 1.1 pro — natural prose, no negative
{
  "model": "flux-1.1-pro",
  "prompt": "A weathered fisherman standing on a wet stone pier at golden hour, soft directional sunlight from the left, holding a hand-painted sign that reads OPEN, shallow depth of field, 35mm film grain.",
  "width": 1024,
  "height": 1024,
  "seed": 42
}

// SDXL base — weighted tags, BREAK, negative prompt
{
  "model": "sdxl-base-1.0",
  "prompt": "(weathered fisherman:1.2), wet stone pier, golden hour, holding (hand-painted sign:1.3) reading OPEN, shallow depth of field BREAK 35mm film, cinematic lighting, photorealistic, sharp focus",
  "negative_prompt": "blurry, deformed hands, extra fingers, watermark, low quality, cartoon, illustration, text artifacts",
  "steps": 28,
  "guidance_scale": 7.0,
  "sampler": "dpmpp_2m",
  "scheduler": "karras",
  "width": 1024,
  "height": 1024,
  "seed": 42
}`}</pre>
				<p>
					If your agent emits one prompt format for both models, one of the two
					results will be worse than necessary. Either branch on model family
					before formatting, or use a hosted endpoint that normalises for you.
					See{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool call patterns
					</Link>{" "}
					for the branching shape we recommend.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					LoRA and finetune ecosystem
				</h2>
				<p>
					SDXL has a three-year head start. Civitai alone hosts tens of
					thousands of LoRAs, and the Illustrious and Pony Diffusion finetunes
					own anime and stylised character work outright. If you need a specific
					aesthetic that someone has already trained — a board-game art style, a
					90s VHS look, a particular illustrator — SDXL almost always has it.
				</p>
				<p>
					FLUX has fewer LoRAs but a much higher signal-to-noise ratio. The ones
					that exist tend to be quality-curated: photorealistic skin, specific
					cinematic looks, brand style transfers. For agents doing generic photo
					or design work, the FLUX base model already covers what you previously
					needed a LoRA for.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Photo vs illustration head-to-head
				</h2>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">Photorealism.</strong> FLUX 1.1
						pro wins on skin, hands, and lighting consistency. SDXL with a good
						photorealism LoRA closes the gap on faces but still produces tells
						in fingers and reflections.
					</li>
					<li>
						<strong className="text-foreground">Stylised illustration.</strong>{" "}
						SDXL via Illustrious or Pony wins. FLUX is competent but homogenises
						styles toward a "FLUX look."
					</li>
					<li>
						<strong className="text-foreground">Embedded text.</strong> FLUX
						wins, full stop. Signs, labels, posters, UI mockups, product
						packaging copy — FLUX renders them. SDXL produces gibberish even
						with negative prompts crafted to suppress it.
					</li>
					<li>
						<strong className="text-foreground">Speed at scale.</strong> SDXL
						Lightning and Turbo run sub-second. FLUX schnell is fast but not
						that fast. For thousands of thumbnails an hour, SDXL Lightning is
						still the floor.
					</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					Cost-quality bands
				</h2>
				<p>
					Roughly: SDXL Lightning sits at the cheap, fast, lower-quality floor.
					Base SDXL and FLUX schnell trade blows in the middle — SDXL wins on
					stylised, schnell wins on prompt adherence and text. FLUX dev raises
					quality at a non-commercial cost. FLUX 1.1 pro is the most expensive
					of the four and the only one a serious customer-facing agent should
					call by default. Live{" "}
					<Link
						to="/pricing"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						per-call pricing
					</Link>{" "}
					is on the pricing page.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Recommended agent setup
				</h2>
				<p>
					Wire three models, not one. Tell the agent the rule explicitly in its
					system prompt — left to itself it will pick whichever model name is
					alphabetically first.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">Drafts.</strong> FLUX schnell at
						4 steps. Cheap enough to throw away.
					</li>
					<li>
						<strong className="text-foreground">
							Finals with text or customer-facing copy.
						</strong>{" "}
						FLUX 1.1 pro.
					</li>
					<li>
						<strong className="text-foreground">
							High-volume batches without embedded text.
						</strong>{" "}
						SDXL Lightning at 6 steps with a populated negative prompt.
					</li>
				</ul>
				<p>
					The agent calls one tool — <code>generate_image</code> — and switches
					the <code>model</code> field. <code>get_generation</code> polls for
					results, <code>get_credits</code> guards the budget. That is the whole
					surface; see the{" "}
					<Link
						to="/help/mcp-tools"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool reference
					</Link>{" "}
					for the parameter shape.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When neither is the right pick
				</h2>
				<p>
					FLUX and SDXL cover most of the workload, but three jobs send them
					both home.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">
							Long, paragraph-length text.
						</strong>{" "}
						FLUX renders short signs and labels well, but multiple lines of
						legible body copy still drift. Ideogram v2 is the model built for
						this.
					</li>
					<li>
						<strong className="text-foreground">
							High-fidelity photographic realism with people.
						</strong>{" "}
						Imagen 3 holds an edge on group shots, real-world locations, and
						skin under hard light. If the brief is "this should look like a
						stock photograph," reach for it.
					</li>
					<li>
						<strong className="text-foreground">
							Stylised editorial illustration.
						</strong>{" "}
						Playground v3 produces cleaner editorial illustration, poster art,
						and graphic-design layouts than either FLUX or SDXL out of the box.
					</li>
				</ul>
				<p>
					A mature agent has all three available alongside FLUX and SDXL and
					routes by intent. Dropping any of them costs you a category of work.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					All of the above on one tool
				</h2>
				<p>
					AgentFramer exposes FLUX schnell, dev, and 1.1 pro alongside the SDXL
					family on a single MCP <code>generate_image</code> tool. The agent
					switches the <code>model</code> field; defaults handle the sampler and
					scheduler per family so you do not have to. Browse the catalogue on
					the model library or read the rest of the{" "}
					<Link
						to="/blog"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						blog
					</Link>{" "}
					for more agent recipes. Pick the model on purpose. The defaults are
					never the right answer for everything.
				</p>
			</div>
		</article>
	);
}
